import { useColorContext } from '@stores/ColorContext';
import { useTheme } from '@stores/ThemeContext';
import { useVersion } from '@utils/hooks/useVersion';

const TestColorWidget = () => {
    const {
        colors,
        loading,
        error,
        refreshColors,
        updateTheme,
        currentTheme,
        isDynamicColors,
        version
    } = useColorContext();

    const { updateDynamicColors } = useTheme();
    const { isV2 } = useVersion();
    if (loading) {
        return <div className="p-8 text-center">Loading colors...</div>;
    }

    if (error) {
        return (
            <div className="p-8 text-center">
                <div className="text-red-600 mb-4">Error: {error}</div>
                <button 
                    onClick={refreshColors}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                    Try again
                </button>
            </div>
        );
    }

    if (!colors) {
        return <div className="p-8 text-center">No colors available</div>;
    }

    return (
        <div className="min-h-screen p-8" style={{ backgroundColor: colors?.backgroundColor }}>
            <div className="max-w-4xl mx-auto">
                <h1 className="text-3xl font-bold mb-8" style={{ color: colors.textColor }}>
                    Theme Colors Test (ColorContext)
                </h1>
                <div className="mb-8 p-6 rounded-lg border-2" style={{ 
                    backgroundColor: colors?.backgroundColor, 
                    borderColor: colors?.borderColor,
                    color: colors?.textColor 
                }}>
                    <h2 className="text-xl font-semibold mb-4">Settings</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block mb-2 font-medium">Theme:</label>
                            <div className="flex space-x-4">
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        value="light"
                                        checked={currentTheme === 'light'}
                                        onChange={() => updateTheme('light')}
                                        className="mr-2"
                                    />
                                    Light
                                </label>
                                <label className="flex items-center">
                                    <input
                                        type="radio"
                                        value="dark"
                                        checked={currentTheme === 'dark'}
                                        onChange={() => updateTheme('dark')}
                                        className="mr-2"
                                    />
                                    Dark
                                </label>
                            </div>
                        </div>
                        
                        <div>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={isDynamicColors}
                                    onChange={(e) => updateDynamicColors(e.target.checked)}
                                    disabled={isV2}
                                    className="mr-2"
                                />
                                Dynamic colors {isV2 && '(forced on for V2)'}
                            </label>
                        </div>
                        
                        <div>
                            <p><strong>Version:</strong> {version}</p>
                        </div>
                    </div>
                </div>

                {/* Color display */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {/* Home Team */}
                    <div className="p-6 rounded-lg border-2" style={{ 
                        backgroundColor: colors.homeColor, 
                        borderColor: colors.borderColor 
                    }}>
                        <h3 className="text-xl font-semibold mb-4" style={{ color: colors.textColor }}>
                            Home Team
                        </h3>
                        <div className="space-y-2">
                            <p style={{ color: colors.textColor }}>
                                <strong>Color:</strong> {colors.homeColor}
                            </p>
                            <p style={{ color: colors.textColor }}>
                                <strong>Sample text:</strong> Lorem ipsum dolor sit amet
                            </p>
                        </div>
                    </div>

                    {/* Away Team */}
                    <div className="p-6 rounded-lg border-2" style={{ 
                        backgroundColor: colors.awayColor, 
                        borderColor: colors.borderColor 
                    }}>
                        <h3 className="text-xl font-semibold mb-4" style={{ color: colors.textColor }}>
                            Away Team
                        </h3>
                        <div className="space-y-2">
                            <p style={{ color: colors.textColor }}>
                                <strong>Color:</strong> {colors.awayColor}
                            </p>
                            <p style={{ color: colors.textColor }}>
                                <strong>Sample text:</strong> Consectetur adipiscing elit
                            </p>
                        </div>
                    </div>
                </div>

                {/* Color information */}
                <div className="mt-8 p-6 rounded-lg border-2" style={{ 
                    backgroundColor: colors?.backgroundColor, 
                    borderColor: colors?.borderColor,
                    color: colors?.textColor 
                }}>
                    <h2 className="text-xl font-semibold mb-4">Color Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <p><strong>Theme:</strong> {currentTheme === 'light' ? 'Light' : 'Dark'}</p>
                            <p><strong>Dynamic colors:</strong> {isDynamicColors ? 'Yes' : 'No'}</p>
                            <p><strong>Background:</strong> {colors?.backgroundColor}</p>
                        </div>
                        <div>
                            <p><strong>Text:</strong> {colors.textColor}</p>
                            <p><strong>Border:</strong> {colors.borderColor}</p>
                        </div>
                    </div>
                </div>

                {/* Refresh button */}
                <div className="mt-8 text-center">
                    <button
                        onClick={refreshColors}
                        className="px-6 py-3 rounded-lg font-medium transition-colors"
                        style={{ 
                            backgroundColor: colors.homeColor, 
                            color: colors.textColor,
                            border: `2px solid ${colors.borderColor}`
                        }}
                    >
                        Refresh colors
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TestColorWidget; 